import { auth } from '@/firebase/firebase';
import { updateSuggestionStatus } from '@/lib/suggestionsV2Client';
import type { SuggestionStatus } from '@/lib/suggestionsV2';
import { setSessionStatus, setSessionStatuses } from '@/lib/suggestionsStatusSession';
import { sendStatusWriteFailureEmail, type StatusFailurePayload } from '@/lib/emailOutbox';

export type UpdateSuggestionStatusArgs = {
  structureId: string;
  suggestionId: string;
  newStatus: SuggestionStatus;
  prevStatus?: SuggestionStatus | null;
  source?: string;
};

export type UpdateSuggestionStatusesArgs = {
  structureId: string;
  statusByItemId: Record<string, SuggestionStatus>;
  source?: string;
};

const getRouteSnapshot = () => {
  if (typeof window === 'undefined') return undefined;
  try {
    return `${window.location.pathname}${window.location.search}`;
  } catch {
    return undefined;
  }
};

const getAppVersion = () => {
  const env = (import.meta as any)?.env ?? {};
  return String(env.VITE_APP_VERSION || env.VITE_LAST_COMMIT_TS || '').trim() || undefined;
};

const sanitizeError = (err: unknown): string => {
  if (!err) return 'unknown_error';
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const anyErr = err as Record<string, unknown>;
    const code = typeof anyErr.code === 'string' || typeof anyErr.code === 'number' ? String(anyErr.code) : '';
    const status = typeof anyErr.status === 'string' || typeof anyErr.status === 'number' ? String(anyErr.status) : '';
    const message = typeof anyErr.message === 'string' ? anyErr.message : '';
    return [code || status, message].filter(Boolean).join(' ') || 'unknown_error';
  }
  return String(err);
};

const buildFailurePayload = (args: {
  structureId: string;
  suggestionId: string;
  newStatus: string;
  prevStatus?: string | null;
  source?: string;
  error: unknown;
  itemCount?: number;
  itemIds?: string[];
}): StatusFailurePayload => ({
  uid: auth.currentUser?.uid ?? null,
  structureId: args.structureId,
  suggestionId: args.suggestionId,
  newStatus: args.newStatus,
  prevStatus: args.prevStatus ?? null,
  route: getRouteSnapshot(),
  timestamp: new Date().toISOString(),
  error: sanitizeError(args.error),
  source: args.source,
  env: (import.meta as any)?.env?.MODE,
  appVersion: getAppVersion(),
  itemCount: args.itemCount,
  itemIds: args.itemIds,
});

export async function updateSuggestionStatusSafe(
  args: UpdateSuggestionStatusArgs
): Promise<{ ok: true; remote: boolean; emailed?: boolean }> {
  const structureId = String(args.structureId ?? '').trim();
  const suggestionId = String(args.suggestionId ?? '').trim();

  if (!structureId || !suggestionId) {
    return { ok: true, remote: false };
  }

  setSessionStatus(structureId, suggestionId, args.newStatus);

  try {
    await updateSuggestionStatus(structureId, {
      statusByItemId: { [suggestionId]: args.newStatus },
    });
    return { ok: true, remote: true };
  } catch (err) {
    const emailed = await sendStatusWriteFailureEmail(
      buildFailurePayload({
        structureId,
        suggestionId,
        newStatus: args.newStatus,
        prevStatus: args.prevStatus ?? null,
        source: args.source,
        error: err,
      })
    );
    return { ok: true, remote: false, emailed };
  }
}

export async function updateSuggestionStatusesSafe(
  args: UpdateSuggestionStatusesArgs
): Promise<{ ok: true; remote: boolean; emailed?: boolean }> {
  const structureId = String(args.structureId ?? '').trim();
  if (!structureId) return { ok: true, remote: false };

  const statusByItemId = args.statusByItemId || {};
  setSessionStatuses(structureId, statusByItemId);

  try {
    await updateSuggestionStatus(structureId, { statusByItemId });
    return { ok: true, remote: true };
  } catch (err) {
    const itemIds = Object.keys(statusByItemId);
    const uniqueStatuses = Array.from(new Set(Object.values(statusByItemId)));
    const newStatus = uniqueStatuses.length === 1 ? uniqueStatuses[0] : 'mixed';
    const emailed = await sendStatusWriteFailureEmail(
      buildFailurePayload({
        structureId,
        suggestionId: 'bulk',
        newStatus,
        prevStatus: 'unknown',
        source: args.source,
        error: err,
        itemCount: itemIds.length,
        itemIds: itemIds.slice(0, 40),
      })
    );
    return { ok: true, remote: false, emailed };
  }
}
